import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Divider,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  AccountBalance as BankIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { ROUTES, ROLES } from "../../constants";
import { employeesAPI } from "../../services/api/employees.api";
import useNotification from "../../hooks/common/useNotification";
import useConfirmDialog from "../../hooks/common/useConfirmDialog";
import { ConfirmDialog } from "../../components/common/Modals";
import EmployeeForm from "../../components/features/employees/EmployeeForm";

// Info Row Component for cleaner display
const InfoRow = ({ icon: Icon, label, value, iconColor }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      py: 2,
      borderBottom: "1px solid rgba(0,0,0,0.04)",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    {Icon && (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: iconColor ? `${iconColor}15` : "rgba(80,118,171,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 2,
        }}
      >
        <Icon sx={{ fontSize: 20, color: iconColor || "#5076ab" }} />
      </Box>
    )}
    <Box flex={1}>
      <Typography
        variant="caption"
        sx={{
          color: "#6e6e73",
          textTransform: "uppercase",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: "#1d1d1f", fontWeight: 500, mt: 0.5 }}
      >
        {value || "Not provided"}
      </Typography>
    </Box>
  </Box>
);

const EmployeeDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyRole, hasRole } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isOpen, config, openDialog, closeDialog, handleConfirm } =
    useConfirmDialog();

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const isEditMode = location.pathname.includes("/edit");

  useEffect(() => {
    const fetchEmployee = async () => {
      if (!id) {
        setError("Employee ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await employeesAPI.getById(id);
        setEmployee(response.data.data || response.data);
      } catch (err) {
        setError(err.userMessage || "Failed to fetch employee details");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [id]);

  const handleEdit = () => {
    navigate(`${ROUTES.EMPLOYEES}/${id}/edit`);
  };

  const handleDelete = () => {
    openDialog({
      title: "Delete Employee",
      message: `Are you sure you want to delete ${employee?.first_name} ${employee?.sur_name}?`,
      variant: "error",
      onConfirm: async () => {
        setDeleteLoading(true);
        try {
          await employeesAPI.delete(id);
          showSuccess("Employee deleted successfully");
          navigate(ROUTES.EMPLOYEES);
        } catch (err) {
          showError("Failed to delete employee");
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "Not provided";
    return new Intl.NumberFormat().format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not provided";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Alert severity="error">{error}</Alert>;

  if (isEditMode) {
    return (
      <EmployeeForm
        editMode={true}
        initialData={employee}
        onSuccess={(updatedEmployee) => {
          setEmployee(updatedEmployee);
          showSuccess("Employee updated successfully");
          navigate(`${ROUTES.EMPLOYEES}/${id}`);
        }}
      />
    );
  }

  const tabs = [
    {
      label: "Basic Info",
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: "#fafafa",
                borderRadius: 2,
                p: 3,
                border: "1px solid rgba(0,0,0,0.04)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1d1d1f",
                }}
              >
                Contact Information
              </Typography>
              <InfoRow icon={EmailIcon} label="Email" value={employee.email} />
              <InfoRow
                icon={PhoneIcon}
                label="Phone"
                value={employee.phone_number}
              />
              <InfoRow
                icon={PersonIcon}
                label="Gender"
                value={employee.gender}
              />
              <InfoRow
                icon={LocationIcon}
                label="Address"
                value={employee.address}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: "#fafafa",
                borderRadius: 2,
                p: 3,
                border: "1px solid rgba(0,0,0,0.04)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1d1d1f",
                }}
              >
                Account Information
              </Typography>
              <InfoRow
                icon={BadgeIcon}
                label="Status"
                value={
                  <Chip
                    label={employee.status}
                    color={getStatusColor(employee.status)}
                    size="small"
                    sx={{ fontWeight: 500, borderRadius: 1.5 }}
                  />
                }
              />
              <InfoRow
                icon={WorkIcon}
                label="Roles"
                value={
                  employee.roles?.map((role) => role.role_name).join(", ") ||
                  "No roles"
                }
              />
              <InfoRow
                icon={CalendarIcon}
                label="Account Created"
                value={formatDate(employee.created_at)}
              />
              <InfoRow
                icon={CalendarIcon}
                label="Last Login"
                value={formatDate(employee.last_login)}
              />
            </Box>
          </Grid>
        </Grid>
      ),
    },
    {
      label: "Personal Info",
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: "#fafafa",
                borderRadius: 2,
                p: 3,
                border: "1px solid rgba(0,0,0,0.04)",
                mb: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1d1d1f",
                }}
              >
                Personal Details
              </Typography>
              <InfoRow
                icon={CalendarIcon}
                label="Date of Birth"
                value={formatDate(employee.bioData?.dob)}
              />
              <InfoRow
                icon={PersonIcon}
                label="Marital Status"
                value={employee.bioData?.marital_status}
              />
              <InfoRow
                label="Blood Group"
                value={employee.bioData?.blood_group}
              />
              <InfoRow
                icon={BadgeIcon}
                label="National ID"
                value={employee.bioData?.national_id}
              />
            </Box>
            <Box
              sx={{
                bgcolor: "#fafafa",
                borderRadius: 2,
                p: 3,
                border: "1px solid rgba(0,0,0,0.04)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1d1d1f",
                }}
              >
                Location & Education
              </Typography>
              <InfoRow
                icon={LocationIcon}
                label="Region"
                value={employee.personalEmployeeData?.region?.name}
              />
              <InfoRow
                icon={LocationIcon}
                label="District"
                value={employee.personalEmployeeData?.district?.name}
              />
              <InfoRow
                icon={SchoolIcon}
                label="Education Level"
                value={employee.personalEmployeeData?.education_level}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: "#fafafa",
                borderRadius: 2,
                p: 3,
                border: "1px solid rgba(0,0,0,0.04)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: "#1d1d1f",
                }}
              >
                Emergency Contacts
              </Typography>
              {employee.emergencyContacts?.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {employee.emergencyContacts.map((contact, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        bgcolor: "white",
                        borderRadius: 2,
                        border: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#1d1d1f", mb: 1 }}
                      >
                        {contact.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <PhoneIcon sx={{ fontSize: 16, color: "#6e6e73" }} />
                        <Typography variant="body2" color="text.secondary">
                          {contact.phone_number}
                        </Typography>
                      </Box>
                      <Chip
                        label={contact.relationship}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          bgcolor: "#5076ab10",
                          color: "#5076ab",
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "#6e6e73", textAlign: "center", py: 4 }}
                >
                  No emergency contacts added
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      ),
    },
    {
      label: "Employment",
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: "#fafafa",
                borderRadius: 2,
                p: 3,
                border: "1px solid rgba(0,0,0,0.04)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1d1d1f",
                }}
              >
                Job Information
              </Typography>
              <InfoRow
                icon={WorkIcon}
                label="Department"
                value={employee.basicEmployeeData?.department?.department_name}
              />
              <InfoRow
                icon={BadgeIcon}
                label="Designation"
                value={employee.basicEmployeeData?.designation}
              />
              <InfoRow
                icon={WorkIcon}
                label="Employment Type"
                value={employee.basicEmployeeData?.employment_type}
              />
              <InfoRow
                icon={CalendarIcon}
                label="Date Joined"
                value={formatDate(employee.basicEmployeeData?.date_joined)}
              />
              <InfoRow
                label="Salary"
                value={`TZS ${formatCurrency(employee.basicEmployeeData?.salary)}`}
              />
              <InfoRow
                icon={PersonIcon}
                label="Supervisor"
                value={
                  employee.basicEmployeeData?.supervisor
                    ? `${employee.basicEmployeeData.supervisor.first_name} ${employee.basicEmployeeData.supervisor.sur_name}`
                    : "Not assigned"
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: "#fafafa",
                borderRadius: 2,
                p: 3,
                border: "1px solid rgba(0,0,0,0.04)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1d1d1f",
                }}
              >
                IDs & Banking
              </Typography>
              <InfoRow
                icon={BadgeIcon}
                label="NIDA"
                value={employee.basicEmployeeData?.nida}
              />
              <InfoRow label="NSSF" value={employee.basicEmployeeData?.nssf} />
              <InfoRow label="BIMA" value={employee.basicEmployeeData?.bima} />
              <InfoRow
                label="HELSB"
                value={employee.basicEmployeeData?.helsb}
              />
              <InfoRow
                icon={BankIcon}
                label="Bank"
                value={employee.basicEmployeeData?.bank_name}
              />
              <InfoRow
                label="Account Number"
                value={employee.basicEmployeeData?.account_number}
              />
            </Box>
          </Grid>
        </Grid>
      ),
    },
    {
      label: "Next of Kin",
      content: (
        <Box
          sx={{
            bgcolor: "#fafafa",
            borderRadius: 2,
            p: 3,
            border: "1px solid rgba(0,0,0,0.04)",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: "#1d1d1f",
            }}
          >
            Next of Kin
          </Typography>
          {employee.nextOfKin?.length > 0 ? (
            <Grid container spacing={2}>
              {employee.nextOfKin.map((kin, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: "white",
                      borderRadius: 2,
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#1d1d1f", mb: 2 }}
                    >
                      {kin.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PhoneIcon sx={{ fontSize: 18, color: "#6e6e73" }} />
                        <Typography variant="body2" color="text.secondary">
                          {kin.phone_number}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                          label={kin.relationship}
                          size="small"
                          sx={{
                            height: 26,
                            fontSize: 12,
                            bgcolor: "#5076ab10",
                            color: "#5076ab",
                            fontWeight: 500,
                          }}
                        />
                        <Chip
                          label={`${kin.percentage}%`}
                          size="small"
                          sx={{
                            height: 26,
                            fontSize: 12,
                            bgcolor: "#f5f5f7",
                            color: "#1d1d1f",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "#6e6e73", textAlign: "center", py: 6 }}
            >
              No next of kin information added
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f5f7",
        pb: 6,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          position: "sticky",
          top: 0,
          zIndex: 100,
          backdropFilter: "blur(20px)",
          backgroundColor: "rgba(255,255,255,0.8)",
        }}
      >
        <Box
          sx={{
            maxWidth: 1400,
            mx: "auto",
            px: 4,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={() => navigate(ROUTES.EMPLOYEES)}
              sx={{
                bgcolor: "#f5f5f7",
                "&:hover": { bgcolor: "#e8e8ed" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#1d1d1f" }}
              >
                {employee.first_name} {employee.middle_name} {employee.sur_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {employee.basicEmployeeData?.designation}
              </Typography>
            </Box>
          </Box>
          {hasAnyRole([ROLES.ADMIN, ROLES.HR_MANAGER]) && (
            <Box display="flex" gap={1.5}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{
                  bgcolor: "#5076ab",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 500,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#3d5f8f",
                    boxShadow: "0 4px 12px rgba(80,118,171,0.2)",
                  },
                }}
              >
                Edit Profile
              </Button>
              {hasRole(ROLES.ADMIN) && (
                <IconButton
                  onClick={handleDelete}
                  sx={{
                    color: "#ff3b30",
                    bgcolor: "#fff5f5",
                    "&:hover": { bgcolor: "#ffe5e5" },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: 1400, mx: "auto", px: 4, pt: 4 }}>
        {/* Profile Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            mb: 3,
            border: "1px solid rgba(0,0,0,0.06)",
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="flex-start" gap={3}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: "#5076ab",
                  fontSize: 36,
                  fontWeight: 500,
                  boxShadow: "0 8px 24px rgba(80,118,171,0.2)",
                }}
              >
                {employee.first_name?.charAt(0)}
                {employee.sur_name?.charAt(0)}
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: "#1d1d1f", mb: 0.5 }}
                >
                  {employee.first_name} {employee.middle_name}{" "}
                  {employee.sur_name}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: "#6e6e73", fontWeight: 400, mb: 2 }}
                >
                  {employee.basicEmployeeData?.designation} •{" "}
                  {employee.basicEmployeeData?.department?.department_name}
                </Typography>
                <Box display="flex" gap={1.5} flexWrap="wrap">
                  <Chip
                    label={employee.status}
                    color={getStatusColor(employee.status)}
                    sx={{
                      fontWeight: 500,
                      borderRadius: 2,
                      height: 28,
                    }}
                  />
                  <Chip
                    icon={<EmailIcon sx={{ fontSize: 16 }} />}
                    label={employee.email}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      height: 28,
                      borderColor: "rgba(0,0,0,0.12)",
                    }}
                  />
                  <Chip
                    icon={<PhoneIcon sx={{ fontSize: 16 }} />}
                    label={employee.phone_number}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      height: 28,
                      borderColor: "rgba(0,0,0,0.12)",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              px: 2,
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: 15,
                fontWeight: 500,
                minHeight: 56,
                color: "#6e6e73",
                "&.Mui-selected": {
                  color: "#5076ab",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#5076ab",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
          <Divider sx={{ borderColor: "rgba(0,0,0,0.06)" }} />
          <Box sx={{ p: 4 }}>{tabs[activeTab]?.content}</Box>
        </Card>
      </Box>

      <ConfirmDialog
        open={isOpen}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        loading={deleteLoading}
        {...config}
      />
    </Box>
  );
};

export default EmployeeDetailsPage;
